import { useState, useEffect } from 'react'
import { Mail, Send, Inbox, RefreshCw, CheckCircle, XCircle } from 'lucide-react'
import api from '../services/api'

export default function GmailTest() {
  const [gmailStatus, setGmailStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [emails, setEmails] = useState([])
  const [selectedEmail, setSelectedEmail] = useState(null)

  // État pour l'envoi d'email
  const [sendForm, setSendForm] = useState({
    to: '',
    subject: '',
    body: ''
  })

  // Vérifier le statut Gmail au chargement
  useEffect(() => {
    checkGmailStatus()
  }, [])

  const checkGmailStatus = async () => {
    try {
      const response = await api.get('/gmail/status')
      setGmailStatus(response.data)
    } catch (error) {
      console.error('Erreur statut Gmail:', error)
    }
  }

  const loadEmails = async () => {
    setLoading(true)
    try {
      const response = await api.get('/gmail/list?maxResults=20')
      if (response.data.success) {
        setEmails(response.data.messages || [])
      }
    } catch (error) {
      console.error('Erreur chargement emails:', error)
      alert('Erreur lors du chargement des emails: ' + (error.response?.data?.error || error.message))
    } finally {
      setLoading(false)
    }
  }

  const viewEmail = async (messageId) => {
    setLoading(true)
    try {
      const response = await api.get(`/gmail/message/${messageId}`)
      if (response.data.success) {
        setSelectedEmail(response.data.email)
      }
    } catch (error) {
      console.error('Erreur lecture email:', error)
      alert('Erreur lors de la lecture de l\'email: ' + (error.response?.data?.error || error.message))
    } finally {
      setLoading(false)
    }
  }

  const sendEmail = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const response = await api.post('/gmail/send', sendForm)
      if (response.data.success) {
        alert('✅ Email envoyé avec succès!')
        setSendForm({ to: '', subject: '', body: '' })
      }
    } catch (error) {
      console.error('Erreur envoi email:', error)
      alert('❌ Erreur lors de l\'envoi: ' + (error.response?.data?.error || error.message))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#0a0a0a', 
      color: '#fff',
      padding: '2rem',
      fontFamily: 'Unbounded, sans-serif'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          background: 'linear-gradient(135deg, rgba(91, 92, 244,0.1), rgba(0,255,255,0.1))',
          border: '1px solid rgba(91, 92, 244,0.2)',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h1 style={{ 
            fontSize: '2rem', 
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            margin: 0
          }}>
            <Mail size={32} style={{ color: '#5b5cf4' }} />
            Gmail Test Panel
          </h1>
          
          {/* Statut Gmail */}
          {gmailStatus && (
            <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {gmailStatus.connected ? (
                <><CheckCircle size={20} style={{ color: '#5b5cf4' }} /> Connecté à Gmail ({gmailStatus.email})</>
              ) : (
                <><XCircle size={20} style={{ color: '#ff4444' }} /> Non connecté - Veuillez vous authentifier avec Google</>
              )}
              <button
                onClick={checkGmailStatus}
                style={{
                  marginLeft: 'auto',
                  background: 'rgba(91, 92, 244,0.1)',
                  border: '1px solid #5b5cf4',
                  color: '#5b5cf4',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <RefreshCw size={16} /> Rafraîchir
              </button>
            </div>
          )}
        </div>

        {!gmailStatus?.connected ? (
          <div style={{
            background: 'rgba(255,68,68,0.1)',
            border: '1px solid #ff4444',
            borderRadius: '12px',
            padding: '2rem',
            textAlign: 'center'
          }}>
            <p>⚠️ Vous devez vous reconnecter avec Google pour activer Gmail.</p>
            <a
              href="http://localhost:8080/api/auth/google"
              style={{
                display: 'inline-block',
                marginTop: '1rem',
                background: '#5b5cf4',
                color: '#fff',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: 600
              }}
            >
              Se connecter avec Google
            </a>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
            {/* Colonne gauche: Envoyer un email */}
            <div>
              <h2 style={{ 
                fontSize: '1.5rem', 
                fontWeight: 600,
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Send size={24} style={{ color: '#5b5cf4' }} />
                Envoyer un email
              </h2>
              
              <form onSubmit={sendEmail} style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '12px',
                padding: '1.5rem'
              }}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#888' }}>Destinataire</label>
                  <input
                    type="email"
                    value={sendForm.to}
                    onChange={(e) => setSendForm({...sendForm, to: e.target.value})}
                    required
                    placeholder="email@example.com"
                    style={{
                      width: '100%',
                      background: 'rgba(0,0,0,0.5)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: '#fff',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#888' }}>Sujet</label>
                  <input
                    type="text"
                    value={sendForm.subject}
                    onChange={(e) => setSendForm({...sendForm, subject: e.target.value})}
                    required
                    placeholder="Sujet de l'email"
                    style={{
                      width: '100%',
                      background: 'rgba(0,0,0,0.5)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: '#fff',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#888' }}>Message</label>
                  <textarea
                    value={sendForm.body}
                    onChange={(e) => setSendForm({...sendForm, body: e.target.value})}
                    required
                    rows={8}
                    placeholder="Contenu de l'email (HTML supporté)"
                    style={{
                      width: '100%',
                      background: 'rgba(0,0,0,0.5)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: '#fff',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      fontFamily: 'monospace',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%',
                    background: loading ? 'rgba(91, 92, 244,0.3)' : '#5b5cf4',
                    color: '#0a0a0a',
                    border: 'none',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    fontWeight: 600,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Send size={20} />
                  {loading ? 'Envoi...' : 'Envoyer'}
                </button>
              </form>
            </div>

            {/* Colonne droite: Lire les emails */}
            <div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                marginBottom: '1rem'
              }}>
                <h2 style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 600,
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Inbox size={24} style={{ color: '#5b5cf4' }} />
                  Boîte de réception
                </h2>
                <button
                  onClick={loadEmails}
                  disabled={loading}
                  style={{
                    background: 'rgba(91, 92, 244,0.1)',
                    border: '1px solid #5b5cf4',
                    color: '#5b5cf4',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <RefreshCw size={16} />
                  {loading ? 'Chargement...' : 'Charger'}
                </button>
              </div>

              {/* Liste des emails */}
              <div style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '12px',
                padding: '1rem',
                maxHeight: '600px',
                overflowY: 'auto'
              }}>
                {emails.length === 0 ? (
                  <p style={{ color: '#888', textAlign: 'center', padding: '2rem' }}>
                    Cliquez sur "Charger" pour voir vos emails
                  </p>
                ) : (
                  emails.map((email) => (
                    <div
                      key={email.id}
                      onClick={() => viewEmail(email.id)}
                      style={{
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        padding: '1rem',
                        marginBottom: '0.5rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(91, 92, 244,0.1)'
                        e.currentTarget.style.borderColor = '#5b5cf4'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.05)'
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'
                      }}
                    >
                      <div style={{ fontSize: '0.875rem', color: '#888' }}>ID: {email.id}</div>
                      <div style={{ fontSize: '0.875rem', color: '#888' }}>Thread: {email.threadId}</div>
                    </div>
                  ))
                )}
              </div>

              {/* Affichage de l'email sélectionné */}
              {selectedEmail && (
                <div style={{
                  marginTop: '1rem',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(91, 92, 244,0.3)',
                  borderRadius: '12px',
                  padding: '1.5rem'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'start',
                    marginBottom: '1rem'
                  }}>
                    <h3 style={{ margin: 0, color: '#5b5cf4' }}>{selectedEmail.subject}</h3>
                    <button
                      onClick={() => setSelectedEmail(null)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#888',
                        cursor: 'pointer',
                        fontSize: '1.5rem'
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#888', marginBottom: '0.5rem' }}>
                    <strong>De:</strong> {selectedEmail.from}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#888', marginBottom: '0.5rem' }}>
                    <strong>À:</strong> {selectedEmail.to}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#888', marginBottom: '1rem' }}>
                    <strong>Date:</strong> {selectedEmail.date}
                  </div>
                  <div style={{ 
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    paddingTop: '1rem',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    {/* HTML d'email = contenu non fiable : rendu dans une iframe
                        sandbox (scripts/formulaires/same-origin désactivés) pour
                        neutraliser toute tentative de XSS (vol du JWT). */}
                    <iframe
                      title="Contenu de l'email"
                      sandbox=""
                      srcDoc={selectedEmail.body || ''}
                      style={{
                        width: '100%',
                        minHeight: '250px',
                        border: 'none',
                        background: '#fff',
                        borderRadius: '6px'
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
